import { useEffect, useRef, useState } from 'react';
import { Loader2, User } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useWhatsAppConfigs } from '../../lib/hooks/useWhatsAppConfigs';
import { WahaApiService } from '../../lib/services/wahaApiService';
import type { WahaConfig } from '../../lib/types/whatsappChat';

/**
 * Input numero di telefono con ricerca while-typing tra i contatti WhatsApp
 * delle sessioni WAHA collegate accessibili all'utente. Se non ci sono sessioni
 * WAHA si comporta come un normale input (nessun dropdown).
 *
 * I contatti vengono caricati lazy al primo focus e cachati a livello modulo
 * (TTL 5 min) — condivisi tra tutte le istanze (modale verifica + Invia Proposta).
 */

interface WaContact {
  /** Numero in cifre, senza +, es. 393331234567 */
  number: string;
  name: string;
  sessionLabel: string;
}

interface CacheEntry {
  contacts: WaContact[];
  ts: number;
}

const CACHE_TTL_MS = 5 * 60 * 1000;
const contactsCache = new Map<string, CacheEntry>();

async function loadContactsForConfig(configId: string, sessionLabel: string): Promise<WaContact[]> {
  const hit = contactsCache.get(configId);
  if (hit && Date.now() - hit.ts < CACHE_TTL_MS) return hit.contacts;

  const { data: cfgRow } = await supabase
    .from('api_configurations')
    .select('id, organization_id, configuration')
    .eq('id', configId)
    .maybeSingle();
  const conf = (cfgRow as any)?.configuration ?? {};
  if (!cfgRow || !conf.session || !conf.api_url) return [];

  const wahaConfig: WahaConfig = {
    apiUrl: conf.api_url,
    // La chiave non serve al client: ogni chiamata passa da waha-proxy, che la
    // inietta lato server. Leggerla qui la esponeva nel browser (07/09/2026).
    apiKey: '',
    session: conf.session,
    phoneNumber: conf.phone_number ?? '',
    organizationId: (cfgRow as any).organization_id ?? '',
    configId: (cfgRow as any).id,
    serviceName: 'waha',
  };
  const raw = await new WahaApiService(wahaConfig).getAllContacts().catch(() => []);
  const contacts: WaContact[] = raw
    .filter((c) => c.id.endsWith('@c.us'))
    .map((c) => ({
      number: c.id.replace('@c.us', ''),
      name: c.name || '',
      sessionLabel,
    }));
  contactsCache.set(configId, { contacts, ts: Date.now() });
  return contacts;
}

interface WaContactSearchInputProps {
  value: string;
  onChange: (v: string) => void;
  /** Chiamato alla selezione di un contatto. number in formato +<cifre>. */
  onPickContact: (number: string, name: string) => void;
  placeholder?: string;
  className?: string;
  /** Classi dell'input (per riusare lo stile del form ospite). */
  inputClassName?: string;
  disabled?: boolean;
}

export function WaContactSearchInput({
  value,
  onChange,
  onPickContact,
  placeholder,
  className,
  inputClassName,
  disabled,
}: WaContactSearchInputProps) {
  const { configs } = useWhatsAppConfigs();
  const wahaConfigs = configs.filter((c) => c.serviceName === 'waha' && c.session);

  const [contacts, setContacts] = useState<WaContact[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState<WaContact[]>([]);
  const debounceRef = useRef<number | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  // Valore corrente per la ri-ricerca post lazy-load (il load può finire dopo il debounce).
  const valueRef = useRef(value);
  valueRef.current = value;

  const multiSession = wahaConfigs.length > 1;

  // Lazy load al primo focus (una volta sola per montaggio; cache modulo tra istanze)
  const ensureContacts = async () => {
    if (contacts !== null || loading || wahaConfigs.length === 0) return;
    setLoading(true);
    try {
      const lists = await Promise.all(
        wahaConfigs.map((c) =>
          loadContactsForConfig(c.id, c.displayName || c.phoneNumber || c.session),
        ),
      );
      const list = lists.flat();
      setContacts(list);
      // Il load può completare DOPO il debounce della prima digitazione:
      // riesegui la ricerca sul testo corrente, altrimenti serve ridigitare.
      const r = search(valueRef.current, list);
      setResults(r);
      if (r.length > 0) setOpen(true);
    } finally {
      setLoading(false);
    }
  };

  // Chiudi il dropdown al click fuori
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  const search = (q: string, list: WaContact[]) => {
    const term = q.trim().toLowerCase();
    const digits = q.replace(/\D/g, '');
    if (term.length < 2 && digits.length < 3) return [];
    return list
      .filter(
        (c) =>
          (term.length >= 2 && c.name.toLowerCase().includes(term)) ||
          (digits.length >= 3 && c.number.includes(digits)),
      )
      .slice(0, 8);
  };

  const handleChange = (v: string) => {
    onChange(v);
    if (wahaConfigs.length === 0) return;
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      const list = contacts ?? [];
      setResults(search(v, list));
      setOpen(true);
    }, 300);
  };

  return (
    <div ref={rootRef} className={`relative ${className ?? ''}`}>
      <input
        type="tel"
        value={value}
        onChange={(e) => handleChange(e.target.value)}
        onFocus={() => {
          if (wahaConfigs.length > 0) setOpen(true);
          void ensureContacts();
        }}
        placeholder={placeholder}
        disabled={disabled}
        className={inputClassName}
      />
      {loading && (
        <Loader2 className="w-3.5 h-3.5 animate-spin text-[#9B9A97] absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
      )}
      {open && results.length === 0 && wahaConfigs.length > 0 && (
        <div className="absolute z-50 left-0 right-0 top-full mt-1 rounded-md border border-[rgba(55,53,47,0.16)] dark:border-[#454B4E] bg-white dark:bg-[#2F3437] shadow-lg px-3 py-2 text-[11px] text-[#6B6762] dark:text-[#9B9A97]">
          {loading
            ? 'Carico i contatti dalle sessioni WhatsApp collegate…'
            : contacts !== null && value.trim().length >= 2
              ? 'Nessun contatto trovato tra le sessioni WhatsApp collegate.'
              : 'Digita un nome o un numero: cerco tra i contatti delle sessioni WhatsApp collegate.'}
        </div>
      )}
      {open && results.length > 0 && (
        <ul className="absolute z-50 left-0 right-0 top-full mt-1 max-h-52 overflow-y-auto rounded-md border border-[rgba(55,53,47,0.16)] dark:border-[#454B4E] bg-white dark:bg-[#2F3437] shadow-lg divide-y divide-black/[0.04] dark:divide-white/[0.05]">
          {results.map((c) => (
            <li key={`${c.sessionLabel}-${c.number}`}>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  setOpen(false);
                  onPickContact(`+${c.number}`, c.name);
                }}
                className="flex items-center gap-2 w-full px-3 py-2 text-left hover:bg-[#2383E2]/[0.06] transition-colors"
              >
                <User className="w-3.5 h-3.5 flex-shrink-0 text-[#9B9A97]" />
                <span className="flex-1 min-w-0">
                  <span className="block text-[13px] text-[#37352F] dark:text-white truncate">
                    {c.name || `+${c.number}`}
                  </span>
                  <span className="block text-[11px] text-[#6B6762] dark:text-[#9B9A97]">
                    +{c.number}
                    {multiSession ? ` · ${c.sessionLabel}` : ''}
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
